<template>
  <b-card class="consistency-widget">
    <div class="d-flex align-items-start justify-content-between consistency-header">
      <div>
        <div class="d-flex align-items-center">
          <div class="section-dot mr-1"></div>
          <h5 class="mb-0">Consistency Issues</h5>
        </div>
        <small class="text-muted">MikroTik vs Billing</small>
      </div>
      <b-button
        size="sm"
        variant="outline-primary"
        :to="{ name: 'isp-consistency-center' }"
        class="consistency-view-btn"
      >
        View
        <feather-icon icon="ArrowRightIcon" size="14" class="ml-50" />
      </b-button>
    </div>

    <div class="summary-strip mt-1">
      <div class="summary-row">
        <div class="summary-value">
          {{ summary.total_issues || 0 }}
        </div>
        <div class="summary-meta">
          <div class="summary-label">Total Issues</div>
          <small class="text-muted" v-if="checkedAt">
            <span class="live-dot mr-25"></span>
            <feather-icon icon="ClockIcon" size="12" class="mr-25" />
            Checked {{ checkedAt }}
          </small>
        </div>
        <b-badge :variant="severity.variant" class="summary-chip">
          {{ severity.label }}
        </b-badge>
      </div>
      <div class="summary-bars">
        <div class="bar-label">Issue Distribution</div>
        <div class="bar-track">
          <div
            v-for="bar in distributionBars"
            :key="bar.key"
            class="bar-segment"
            :style="{ width: bar.width, background: bar.color }"
          ></div>
        </div>
      </div>
    </div>

    <div class="consistency-badges mt-1">
      <b-badge
        v-for="item in badgeItems"
        :key="item.key"
        :variant="item.variant"
        class="consistency-badge"
        role="button"
        @click="goToIssue(item.issue)"
      >
        <feather-icon :icon="item.icon" size="12" class="mr-25" />
        <span>{{ item.label }}: {{ summary[item.key] || 0 }}</span>
      </b-badge>
    </div>

    <div v-if="isLoading" class="text-muted small mt-1">Refreshing...</div>
  </b-card>
</template>

<script>
import { BCard, BButton, BBadge } from 'bootstrap-vue'
import { CONSISTENCY_SUMMARY } from '@core/services/api'

export default {
  components: {
    BCard,
    BButton,
    BBadge,
  },
  data() {
    return {
      isLoading: false,
      checkedAt: null,
      summary: {
        missing_in_router: 0,
        missing_in_billing: 0,
        router_mismatch: 0,
        status_mismatch: 0,
        profile_mismatch: 0,
        total_issues: 0,
      },
    }
  },
  computed: {
    badgeItems() {
      return [
        { key: 'missing_in_router', label: 'Missing Router', variant: 'light-danger', issue: 'missing_in_router', icon: 'AlertTriangleIcon' },
        { key: 'missing_in_billing', label: 'Missing Billing', variant: 'light-warning', issue: 'missing_in_billing', icon: 'UserXIcon' },
        { key: 'router_mismatch', label: 'Router Mismatch', variant: 'light-primary', issue: 'router_mismatch', icon: 'ShuffleIcon' },
        { key: 'status_mismatch', label: 'Status Mismatch', variant: 'light-info', issue: 'status_mismatch', icon: 'SlashIcon' },
        { key: 'profile_mismatch', label: 'Profile Mismatch', variant: 'light-secondary', issue: 'profile_mismatch', icon: 'TargetIcon' },
      ]
    },
    severity() {
      const total = Number(this.summary.total_issues || 0)
      if (total === 0) return { label: 'Clean', variant: 'light-success' }
      if (total >= 200) return { label: 'High', variant: 'light-danger' }
      if (total >= 50) return { label: 'Medium', variant: 'light-warning' }
      return { label: 'Low', variant: 'light-info' }
    },
    distributionBars() {
      const total = Number(this.summary.total_issues || 0)
      const items = [
        { key: 'missing_in_router', color: '#fca5a5' },
        { key: 'missing_in_billing', color: '#fdba74' },
        { key: 'router_mismatch', color: '#c7d2fe' },
        { key: 'status_mismatch', color: '#67e8f9' },
        { key: 'profile_mismatch', color: '#e5e7eb' },
      ]

      if (!total) {
        return items.map(item => ({ ...item, width: '0%' }))
      }

      return items.map(item => {
        const count = Number(this.summary[item.key] || 0)
        const width = `${Math.max((count / total) * 100, count ? 2 : 0)}%`
        return { ...item, width }
      })
    },
  },
  mounted() {
    this.fetchSummary()
  },
  methods: {
    fetchSummary() {
      this.isLoading = true
      this.$http
        .get(process.env.VUE_APP_BASEURL + CONSISTENCY_SUMMARY)
        .then(res => {
          const data = res.data.data || {}
          this.summary = data.summary || this.summary
          this.checkedAt = data.checked_at || null
        })
        .catch(() => {
          this.summary = {
            missing_in_router: 0,
            missing_in_billing: 0,
            router_mismatch: 0,
            status_mismatch: 0,
            profile_mismatch: 0,
            total_issues: 0,
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    goToIssue(issue) {
      if (!issue) return
      this.$router.push({ name: 'isp-consistency-center', query: { issue } })
    },
  },
}
</script>

<style lang="scss">
.consistency-widget {
  position: relative;
  overflow: hidden;
  border: 1px solid #e7ebf5;
  box-shadow: 0 14px 28px rgba(16, 24, 40, 0.08);
  border-radius: 16px;
  background:
    radial-gradient(circle at 12% 10%, rgba(99, 102, 241, 0.12), transparent 45%),
    linear-gradient(180deg, #f6f9ff 0%, #ffffff 38%);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 30px rgba(16, 24, 40, 0.12);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #6366f1, #38bdf8);
    opacity: 0.55;
  }

  &::before {
    content: '';
    position: absolute;
    right: -80px;
    top: -80px;
    width: 220px;
    height: 220px;
    border-radius: 999px;
    background: radial-gradient(circle, rgba(56, 189, 248, 0.25), transparent 70%);
    opacity: 0.8;
    pointer-events: none;
  }

  .consistency-header {
    padding-bottom: 0.45rem;
    border-bottom: 1px solid #f0f2f6;
  }

  .consistency-header h5 {
    font-weight: 700;
    color: #1f2937;
    letter-spacing: 0.01em;
  }

  .consistency-header small {
    color: #64748b;
  }

  .section-dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(135deg, #0ea5e9, #38bdf8);
    box-shadow: 0 0 0 6px rgba(14, 165, 233, 0.12);
    animation: dot-pulse 2.6s ease-in-out infinite;
  }

  .summary-strip {
    padding: 0.4rem;
    border-radius: 14px;
    background: rgba(255, 255, 255, 0.75);
    border: 1px solid #edf2ff;
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 0.75rem;
    align-items: center;
  }

  .summary-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.65rem 0.85rem;
    border-radius: 12px;
    background: linear-gradient(135deg, #ffffff 0%, #f7f8ff 100%);
    border: 1px solid #e6eaf5;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
    box-shadow: 0 10px 20px rgba(99, 102, 241, 0.08);
  }

  .summary-value {
    font-size: 2.35rem;
    font-weight: 700;
    color: #1d4ed8;
    line-height: 1;
    padding: 0.35rem 0.8rem;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba(29, 78, 216, 0.18), rgba(56, 189, 248, 0.16));
    box-shadow: 0 8px 16px rgba(29, 78, 216, 0.16);
    text-shadow: 0 1px 0 rgba(255, 255, 255, 0.6);
  }

  .summary-meta {
    display: flex;
    flex-direction: column;
  }

  .summary-label {
    font-weight: 600;
    color: #374151;
  }

  .summary-chip {
    margin-left: auto;
    padding: 0.35rem 0.7rem;
    font-weight: 600;
    border-radius: 999px;
    box-shadow: 0 6px 14px rgba(15, 23, 42, 0.12);
  }

  .live-dot {
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #22c55e;
    box-shadow: 0 0 0 4px rgba(34, 197, 94, 0.2);
    animation: live-pulse 2s ease-in-out infinite;
  }

  .summary-bars {
    padding: 0.6rem 0.75rem;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.06), rgba(56, 189, 248, 0.05));
    border: 1px solid rgba(148, 163, 184, 0.25);
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  .bar-label {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
    margin-bottom: 0.35rem;
  }

  .bar-track {
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(90deg, #eef2ff, #e0f2fe);
    overflow: hidden;
    display: flex;
    box-shadow: inset 0 2px 6px rgba(15, 23, 42, 0.08);
  }

  .bar-segment {
    height: 100%;
    transition: width 0.2s ease;
  }

  .consistency-badges {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    gap: 0.6rem;
    padding: 0.65rem 0.75rem;
    border-radius: 12px;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border: 1px solid #e6eaf5;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }

  .consistency-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    padding: 0.35rem 0.7rem;
    border-radius: 999px;
    cursor: pointer;
    border: 1px solid rgba(148, 163, 184, 0.2);
    transition: transform 0.15s ease, box-shadow 0.15s ease;
    box-shadow: 0 6px 12px rgba(15, 23, 42, 0.08);
  }

  .consistency-badge.badge-light-danger {
    background: linear-gradient(135deg, #fee2e2, #fecaca);
    border-color: rgba(248, 113, 113, 0.6);
    color: #b91c1c;
    box-shadow: 0 8px 18px rgba(248, 113, 113, 0.28);
  }

  .consistency-badge.badge-light-warning {
    background: linear-gradient(135deg, #ffedd5, #fed7aa);
    border-color: rgba(251, 146, 60, 0.6);
    color: #c2410c;
    box-shadow: 0 8px 18px rgba(251, 146, 60, 0.28);
  }

  .consistency-badge.badge-light-primary {
    background: linear-gradient(135deg, #e0e7ff, #c7d2fe);
    border-color: rgba(99, 102, 241, 0.6);
    color: #4338ca;
    box-shadow: 0 8px 18px rgba(99, 102, 241, 0.28);
  }

  .consistency-badge.badge-light-info {
    background: linear-gradient(135deg, #cffafe, #a5f3fc);
    border-color: rgba(34, 211, 238, 0.6);
    color: #0e7490;
    box-shadow: 0 8px 18px rgba(34, 211, 238, 0.28);
  }

  .consistency-badge.badge-light-secondary {
    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
    border-color: rgba(148, 163, 184, 0.7);
    color: #475569;
    box-shadow: 0 8px 18px rgba(148, 163, 184, 0.24);
  }

  .consistency-badge:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 12px 22px rgba(15, 23, 42, 0.18);
  }

  .consistency-badge::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.5), transparent 60%);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;
  }

  .consistency-badge:hover::after {
    opacity: 1;
  }

  .consistency-view-btn {
    border-radius: 999px;
    padding: 0.3rem 0.85rem;
  }

  @media (max-width: 991px) {
    .summary-strip {
      grid-template-columns: 1fr;
    }
  }
}

@keyframes dot-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(14, 165, 233, 0.2);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(14, 165, 233, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(14, 165, 233, 0);
  }
}

@keyframes live-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.3);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(34, 197, 94, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
  }
}
</style>
